import { ONE_DAY_MS } from '@eflight/shared';
import { API, graphqlOperation } from 'aws-amplify';

import {
    type AirportCategorization,
    airportCategorizationFromDb,
} from '../../../Admin/AirportCategorizations/AirportCategorization';
import { type QueryResult } from '../../../Admin/Datasources/QueryFactory';
import { type AirportProviders } from '../../../Admin/screens/Airports/Providers/AirportProviderTypes';
import {
    type CreateAirportCategorizationInput,
    type CreateAirportInput,
    type CreateAirportRunwayInput,
} from '../../../API';
import { type LocalDB } from '../../LocalDB/LocalDB';
import { type DbCreated, type DbItems, type WithId } from '../../LocalDB/types';
import { type FlightSupplementalAirport, FlightSupplementalDataType } from '../FlightSupplementalDataTypes';

type AirportCategorizationPick = Pick<AirportCategorization, 'categorizationData'>;
/** What the flight weather validation reads about a runway (see WeatherValidationAirport). */
type SupplementalDataRunway = Pick<
    CreateAirportRunwayInput,
    | 'closed'
    | 'length'
    | 'surface'
    | 'thresholdLatitudeHigh'
    | 'thresholdLatitudeLow'
    | 'thresholdLongitudeHigh'
    | 'thresholdLongitudeLow'
>;
export type SupplementalDataAirport = Pick<
    CreateAirportInput & WithId,
    | 'elevation'
    | 'icao'
    | 'id'
    | 'instrument'
    | 'instrumentLowestCeilingFt'
    | 'instrumentLowestRvrM'
    | 'latitude'
    | 'longitude'
    | 'name'
    | 'night'
    | 'refuelEu'
    | 'suitableToAlternate'
    | 'timezone'
> & {
    providersData: AirportProviders;
    categorization: AirportCategorizationPick | null;
    /** Absent on a copy stored before runways were kept; the next supplemental sync fills them in. */
    runways?: DbItems<SupplementalDataRunway>;
};

type QueryAirportReturn = SupplementalDataAirport & {
    categorizations: DbItems<Pick<CreateAirportCategorizationInput, 'categorizationData'>>;
};

export async function loadAirports(db: LocalDB, icaos: string[]): Promise<void> {
    const safeIcaos = icaos.map((i) => i.toUpperCase().replaceAll(/[^\dA-Z]/g, '')).filter((i) => i.length);
    if (!safeIcaos.length) {
        return;
    }
    const subqueries = safeIcaos.map(
        // index alias: a GraphQL alias can't start with a digit (e.g. FAA-style "06FA")
        (icao, index) => `
            a${index}: listAirportsByIcao(icao: "${icao}") {
                items {
                    id
                    icao
                    name
                    latitude
                    longitude
                    instrument
                    instrumentLowestCeilingFt
                    instrumentLowestRvrM
                    night
                    elevation
                    timezone
                    suitableToAlternate
                    providersData
                    refuelEu
                    runways {
                        items {
                            closed
                            length
                            surface
                            thresholdLatitudeHigh
                            thresholdLatitudeLow
                            thresholdLongitudeHigh
                            thresholdLongitudeLow
                        }
                    }
                    categorizations {
                        items {
                            categorizationData
                        }
                    }
                }
            }`
    );
    const query = `
        query AirportMultiQuery {
            ${subqueries.join('\n')}
        }
    `;
    const result = (await API.graphql(graphqlOperation(query))) as QueryResult<{
        [item: string]: DbItems<QueryAirportReturn>;
    }>;
    const airports = Object.values(result.data).flatMap((i) => i.items.map(airportToSupplementalData));
    await db.flightSupplementalData.bulkPut(airports);
}

function airportToSupplementalData(airport: QueryAirportReturn): FlightSupplementalAirport {
    const maybeCategorization = airport.categorizations.items[0]
        ? (airportCategorizationFromDb(
              airport.categorizations.items[0] as CreateAirportCategorizationInput & DbCreated
          ) as AirportCategorizationPick)
        : null;

    return {
        id: getSupplementalAirportDbId(airport.icao),
        usefulnessEndsByDate: new Date(Date.now() + 30 * ONE_DAY_MS),
        type: FlightSupplementalDataType.airport,
        airport: {
            ...airport,
            providersData: JSON.parse(airport.providersData as unknown as string),
            categorization: maybeCategorization,
        },
    };
}

export function getSupplementalAirportDbId(icao: string): string {
    return `apt-${icao.toLowerCase()}`;
}
